import { useEffect, useRef, useState } from 'react';
import { ISearchStudy } from '@vivli/features/dashboard/infrastructure/interface';
import { first } from 'rxjs/operators';
import { IFilterSet, IQuickSearchApi } from '@vivli/features/search/infrastructure/interface';
import { NoSearchResultsFoundModalComponent } from '@vivli/shared/components';
import { IModalApi } from '@vivli/shared/infrastructure/interface';
import { AssignedAppTypeEnum } from '@vivli/shared/infrastructure/enum';
import { IAzureSearch } from '@vivli/core/infrastructure/interface';

interface IQuickSearchApiProps {
    modalApi: IModalApi;
    assignedAppType: AssignedAppTypeEnum;
    studiesSearch: IAzureSearch;
}

export const useQuickSearch = ({ modalApi, assignedAppType, studiesSearch }: IQuickSearchApiProps): IQuickSearchApi => {
    const [isShowingResults, setIsShowingResults] = useState(false);
    const [searchResults, setSearchResults] = useState<ISearchStudy[]>([]);
    const [isGettingStudies, setIsGettingStudies] = useState(false);
    const [searchText, setSearchText] = useState('');

    const filterSetsRef = useRef<IFilterSet[]>([]);

    const updateStudyData = () => {
        setIsGettingStudies(true);
        studiesSearch
            .search<ISearchStudy, any>({
                filterSets: filterSetsRef.current,
                assignedAppType,
            })
            .pipe(first())
            .subscribe(({ result }) => {
                setSearchResults(result);
                setIsGettingStudies(false);
                if (result.length < 1) {
                    const modalId = modalApi.custom(
                        <NoSearchResultsFoundModalComponent
                            onClose={() => modalApi.dismiss(modalId)}
                            noneFoundMessage={'No quick lookup results found.'}
                        />,
                        {
                            showBackground: true,
                        }
                    );
                }
            });
    };

    const addFilterSet = (filterSet: IFilterSet) => {
        /* clear out old failed queries or queries for a different id type */
        filterSetsRef.current = [];
        filterSetsRef.current = [...filterSetsRef.current, filterSet];
        updateStudyData();
    };

    const closeResults = () => {
        setIsShowingResults(false);
        setSearchText('');
        setSearchResults([]);
    };

    useEffect(() => {
        setIsGettingStudies(true);
        setIsShowingResults(searchResults?.length > 0);
    }, [searchResults]);

    return {
        isGettingStudies,
        isShowingResults,
        searchResults,
        addFilterSet,
        searchText,
        setSearchText,
        closeResults,
    };
};
